(() => {
  const STYLE_ID = 'oriavi-format-direct-select-style';
  const DESKTOP = window.matchMedia('(min-width: 990px)');
  const REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)');
  const PANORAMIC_URL = 'https://cdn.shopify.com/s/files/1/0114/3613/0370/products/001_8adf5a27-1de5-4b62-a3ce-562ebfc7b7aa.jpg?v=1746138570';

  const addWidth = (url, width) => {
    try {
      const parsed = new URL(url, window.location.origin);
      parsed.searchParams.set('width', width);
      return parsed.toString();
    } catch (_) {
      return url;
    }
  };

  const preservePreview = (href) => {
    try {
      const current = new URL(window.location.href);
      const target = new URL(href, window.location.href);
      ['preview_theme_id', '_ab', '_fd', '_sc'].forEach((key) => {
        if (current.searchParams.has(key) && !target.searchParams.has(key)) {
          target.searchParams.set(key, current.searchParams.get(key));
        }
      });
      return target.toString();
    } catch (_) {
      return href;
    }
  };

  const ensurePanoramicCard = (grid) => {
    let panoramic = grid.querySelector('.oriavi-home__format--panoramic');
    if (panoramic) return panoramic;

    const source = grid.querySelector('.oriavi-home__format--landscape') || grid.querySelector('.oriavi-home__format:last-child');
    if (!source) return null;

    panoramic = source.cloneNode(true);
    [...panoramic.classList].forEach((name) => {
      if (name.startsWith('oriavi-home__format--')) panoramic.classList.remove(name);
    });
    panoramic.classList.add('oriavi-home__format--panoramic');
    panoramic.href = '/collections/persian-wall-art/Panoramic?sort_by=best-selling';
    panoramic.removeAttribute('aria-current');
    panoramic.removeAttribute('data-oriavi-stack-depth');
    panoramic.style.removeProperty('--oriavi-stack-slot');
    panoramic.style.removeProperty('--oriavi-stack-depth');

    const title = panoramic.querySelector('.oriavi-home__format-copy strong');
    const copy = panoramic.querySelector('.oriavi-home__format-copy small');
    if (title) title.textContent = 'Panoramic';
    if (copy) copy.textContent = 'Wide-format art for expansive walls';

    const image = panoramic.querySelector('img');
    if (image) {
      const widths = [480, 640, 720, 800, 960, 1200];
      image.src = addWidth(PANORAMIC_URL, 1200);
      image.srcset = widths.map((width) => `${addWidth(PANORAMIC_URL, width)} ${width}w`).join(', ');
      image.sizes = '(min-width: 990px) 30vw, calc(100vw - 36px)';
      image.alt = 'Panoramic Persian wall art by ORIAVI';
      image.loading = 'lazy';
      image.removeAttribute('fetchpriority');
    }

    grid.appendChild(panoramic);
    return panoramic;
  };

  const neutralizeLegacyStack = (grid) => {
    grid.removeAttribute('data-oriavi-stack-ready');
    grid.querySelectorAll(':scope > .oriavi-home__format').forEach((card) => {
      card.removeAttribute('data-oriavi-stack-depth');
      card.removeAttribute('aria-current');
      card.style.removeProperty('--oriavi-stack-slot');
      card.style.removeProperty('--oriavi-stack-depth');
    });
  };

  const injectStyles = () => {
    if (document.getElementById(STYLE_ID)) return;
    const style = document.createElement('style');
    style.id = STYLE_ID;
    style.textContent = `
      /* Retire the old 3-card stack helper. Its ::after label was the source of “Bring forward”. */
      body.template-index .oriavi-home__format-grid > .oriavi-home__format::after {
        content: none !important;
        display: none !important;
      }

      @media screen and (min-width: 990px) {
        body.template-index .oriavi-home__format-grid {
          position: relative !important;
          display: block !important;
          grid-template-columns: none !important;
          grid-template-rows: none !important;
          min-height: clamp(430px, 40vw, 610px) !important;
          margin-top: clamp(30px, 3.5vw, 54px) !important;
          overflow: visible !important;
          isolation: isolate;
        }

        body.template-index .oriavi-home__format-grid > .oriavi-home__format {
          --oriavi-card-left: 50%;
          --oriavi-card-top: 54%;
          --oriavi-card-rotate: 0deg;
          --oriavi-card-scale: 1;
          position: absolute !important;
          left: var(--oriavi-card-left) !important;
          top: var(--oriavi-card-top) !important;
          width: 30% !important;
          height: auto !important;
          aspect-ratio: 1 / 1 !important;
          min-height: 0 !important;
          max-height: none !important;
          margin: 0 !important;
          grid-row: auto !important;
          grid-column: auto !important;
          transform: translate(-50%, -50%) rotate(var(--oriavi-card-rotate)) scale(var(--oriavi-card-scale)) !important;
          transform-origin: center center !important;
          z-index: var(--oriavi-card-z, 1) !important;
          overflow: hidden !important;
          box-shadow: 0 18px 46px rgba(24, 18, 16, .18), 0 5px 14px rgba(24, 18, 16, .10);
          will-change: left, top, transform, filter, opacity;
          transition:
            left .66s cubic-bezier(.2,.82,.2,1),
            top .66s cubic-bezier(.2,.82,.2,1),
            transform .72s cubic-bezier(.16,.86,.22,1),
            box-shadow .55s ease,
            filter .55s ease,
            opacity .55s ease !important;
        }

        body.template-index .oriavi-home__format-grid > .oriavi-home__format:nth-child(1) {
          --oriavi-card-left: 18%;
          --oriavi-card-top: 54%;
          --oriavi-card-rotate: -2.6deg;
          --oriavi-card-z: 1;
        }
        body.template-index .oriavi-home__format-grid > .oriavi-home__format:nth-child(2) {
          --oriavi-card-left: 39%;
          --oriavi-card-top: 42%;
          --oriavi-card-rotate: -.9deg;
          --oriavi-card-z: 3;
        }
        body.template-index .oriavi-home__format-grid > .oriavi-home__format:nth-child(3) {
          --oriavi-card-left: 61%;
          --oriavi-card-top: 62%;
          --oriavi-card-rotate: .9deg;
          --oriavi-card-z: 4;
        }
        body.template-index .oriavi-home__format-grid > .oriavi-home__format:nth-child(4) {
          --oriavi-card-left: 82%;
          --oriavi-card-top: 54%;
          --oriavi-card-rotate: 2.6deg;
          --oriavi-card-z: 2;
        }

        /* Square overlaps the left edge of Panoramic, so keep Panoramic copy on its exposed right side. */
        body.template-index .oriavi-home__format-grid > .oriavi-home__format--panoramic .oriavi-home__format-copy {
          left: auto !important;
          right: clamp(22px, 2.1vw, 34px) !important;
          bottom: clamp(22px, 2.4vw, 38px) !important;
          max-width: 72% !important;
          align-items: flex-end !important;
          text-align: right !important;
          z-index: 6 !important;
        }

        body.template-index .oriavi-home__format-grid > .oriavi-home__format--panoramic .oriavi-home__format-copy strong {
          font-size: clamp(30px, 3vw, 50px) !important;
          white-space: nowrap;
        }

        body.template-index .oriavi-home__format-grid > .oriavi-home__format--panoramic .oriavi-home__format-copy small {
          max-width: 220px;
        }

        body.template-index .oriavi-home__format-grid:not(.oriavi-format-is-transitioning) > .oriavi-home__format:hover,
        body.template-index .oriavi-home__format-grid:not(.oriavi-format-is-transitioning) > .oriavi-home__format:focus-visible {
          --oriavi-card-scale: 1.035;
          z-index: 18 !important;
          box-shadow: 0 28px 64px rgba(24, 18, 16, .24), 0 8px 20px rgba(24, 18, 16, .13);
        }

        body.template-index .oriavi-home__format-grid > .oriavi-home__format img {
          width: 100% !important;
          height: 100% !important;
          object-fit: cover !important;
          transform: scale(1) !important;
          transition: transform .78s cubic-bezier(.16,.82,.2,1), filter .55s ease !important;
          will-change: transform;
        }

        body.template-index .oriavi-home__format-grid.oriavi-format-is-transitioning > .oriavi-home__format:not(.oriavi-format-is-active) {
          --oriavi-card-scale: .86;
          filter: brightness(.68) saturate(.82);
          opacity: .7;
          pointer-events: none;
        }

        body.template-index .oriavi-home__format-grid > .oriavi-home__format.oriavi-format-is-active {
          --oriavi-card-left: 50% !important;
          --oriavi-card-top: 50% !important;
          --oriavi-card-rotate: 0deg !important;
          --oriavi-card-scale: 1.08 !important;
          z-index: 90 !important;
          filter: none !important;
          opacity: 1 !important;
          box-shadow: 0 38px 92px rgba(19, 14, 12, .34), 0 10px 28px rgba(19, 14, 12, .16) !important;
          pointer-events: none;
        }

        body.template-index .oriavi-home__format-grid.oriavi-format-zoom > .oriavi-home__format.oriavi-format-is-active {
          --oriavi-card-scale: 1.34 !important;
          box-shadow: 0 48px 118px rgba(19, 14, 12, .40), 0 12px 34px rgba(19, 14, 12, .18) !important;
        }

        body.template-index .oriavi-home__format-grid.oriavi-format-zoom > .oriavi-home__format.oriavi-format-is-active img {
          transform: scale(1.13) !important;
        }

        body.template-index .oriavi-home__format-grid.oriavi-format-final > .oriavi-home__format.oriavi-format-is-active {
          --oriavi-card-scale: 1.41 !important;
        }

        body.template-index .oriavi-home__format-grid.oriavi-format-final > .oriavi-home__format.oriavi-format-is-active img {
          transform: scale(1.18) !important;
        }

        body.template-index .oriavi-home__format-grid.oriavi-format-final > .oriavi-home__format:not(.oriavi-format-is-active) {
          opacity: .48;
          filter: brightness(.58) saturate(.7);
        }

        body.template-index .oriavi-home__format-grid > .oriavi-home__format .oriavi-home__format-copy,
        body.template-index .oriavi-home__format-grid > .oriavi-home__format .oriavi-home__format-arrow {
          transition: transform .55s cubic-bezier(.2,.8,.2,1), opacity .45s ease !important;
        }

        body.template-index .oriavi-home__format-grid.oriavi-format-zoom > .oriavi-home__format.oriavi-format-is-active .oriavi-home__format-copy {
          transform: translateY(-8px);
        }

        body.template-index .oriavi-home__format-grid.oriavi-format-zoom > .oriavi-home__format.oriavi-format-is-active .oriavi-home__format-arrow {
          transform: translate(5px,-5px) scale(1.08);
        }
      }

      @media (prefers-reduced-motion: reduce) and (min-width: 990px) {
        body.template-index .oriavi-home__format-grid > .oriavi-home__format,
        body.template-index .oriavi-home__format-grid > .oriavi-home__format img {
          transition-duration: .01ms !important;
        }
      }
    `;
    document.head.appendChild(style);
  };

  const clearState = (grid) => {
    grid.classList.remove('oriavi-format-is-transitioning', 'oriavi-format-zoom', 'oriavi-format-final');
    grid.querySelectorAll('.oriavi-format-is-active').forEach((card) => card.classList.remove('oriavi-format-is-active'));
    grid.dataset.oriaviNavigating = '';
  };

  const bindDesktopMotion = (grid) => {
    if (grid.dataset.oriaviFourCardBound === 'true') return;
    grid.dataset.oriaviFourCardBound = 'true';

    grid.addEventListener('click', (event) => {
      const card = event.target.closest('.oriavi-home__format');
      if (!card || !grid.contains(card) || !DESKTOP.matches) return;
      if (event.defaultPrevented || event.button > 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      if (grid.dataset.oriaviNavigating === 'true') {
        event.preventDefault();
        return;
      }

      const cards = [...grid.querySelectorAll(':scope > .oriavi-home__format')];
      if (cards.length !== 4) return;

      const href = preservePreview(card.getAttribute('href') || card.href);
      if (!href) return;

      event.preventDefault();
      if (REDUCED.matches) {
        window.location.assign(href);
        return;
      }

      grid.dataset.oriaviNavigating = 'true';
      grid.classList.add('oriavi-format-is-transitioning');
      card.classList.add('oriavi-format-is-active');

      window.setTimeout(() => grid.classList.add('oriavi-format-zoom'), 610);
      window.setTimeout(() => grid.classList.add('oriavi-format-final'), 1260);
      window.setTimeout(() => window.location.assign(href), 1660);
    });

    window.addEventListener('pageshow', () => clearState(grid));
  };

  const init = () => {
    injectStyles();
    document.querySelectorAll('.oriavi-home__format-grid').forEach((grid) => {
      neutralizeLegacyStack(grid);
      ensurePanoramicCard(grid);
      neutralizeLegacyStack(grid);
      const cards = [...grid.querySelectorAll(':scope > .oriavi-home__format')];
      if (cards.length !== 4) return;
      bindDesktopMotion(grid);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
